import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Home, Users, MessageCircle, Menu as MenuIcon, Calendar } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { NAV } from "@/lib/nav";
import { usePermission } from "@/hooks/usePermission";
import { useAccessRequestsBadge } from "@/hooks/useAccessRequestsBadge";
import { useModuleVisibility } from "@/store/moduleVisibility";
import { Logo } from "@/components/Logo";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

/** Humanize a translation key when the translator returned the raw key. */
function navLabel(t, key) {
  const val = t(key);
  if (val && val !== key) return val;
  const leaf = key.split(".").pop() || key;
  return leaf.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase()).trim();
}

/** Mobile bottom-tab bar (<768px). 5 tabs max — last opens drawer. */
export function BottomTabBar() {
  const { t } = useTranslation();
  const loc = useLocation();
  const canUsers = usePermission("users", "view");
  const canAudit = usePermission("audit", "view");
  const canViewAdmin = canUsers || canAudit;
  // Phase 8A — module visibility matrix.
  // Phase 8B (bugfix): read preview overlay if set so the mobile bottom tabs
  // stay consistent with the sidebar during preview.
  const modules = useModuleVisibility((s) => s.previewModules || s.modules);
  const loaded = useModuleVisibility((s) => s.loaded);
  const { approverPending: accessRequestsPending } = useAccessRequestsBadge();   // D55d-uat3

  // Phase 20-tail (post hub-collapse) -- mobile IA reset. Sidebar dropped
  // from ~30 top-level entries to ~10, so the bottom 5 rethinks around the
  // highest-frequency roles-agnostic destinations:
  //   Home | Calendar (P21, greyed until Phase 21 lands) | Chat | HR | More
  // Sales dropped from primary since it's role-specific; anyone who needs it
  // still gets it via "More" (drawer keeps the full NAV list).
  const allTabs = [
    { key: "home",     moduleKey: "home",     labelKey: "nav.home",     icon: Home,           to: "/",         comingPhase: null },
    { key: "calendar", moduleKey: "calendar", labelKey: "nav.calendar", icon: Calendar,        to: "/calendar", comingPhase: 21 },
    { key: "chat",     moduleKey: "chat",     labelKey: "nav.chat",     icon: MessageCircle,   to: "/chat",     comingPhase: null },
    { key: "hr",       moduleKey: "hr",       labelKey: "nav.hr",       icon: Users,           to: "/hr",       comingPhase: null },
  ];
  const tabs = allTabs.filter((tab) => {
    if (tab.key === "home") return true;
    // Coming-in-Phase-X tabs stay visible (greyed) even if the matrix has no
    // key yet -- they render a "coming soon" toast on tap.
    if (tab.comingPhase) return true;
    if (!loaded || !modules) return true;
    return modules[tab.moduleKey] === true;
  });

  const visible = NAV.filter((m) => {
    if (m.key === "home") return true;
    if (m.key === "admin" && !canViewAdmin) return false;
    // D55d-uat3 -- Approver-only entries (Access Requests) hidden until
    // the user has pending approvals to review.
    if (m.approverOnly && accessRequestsPending <= 0) return false;
    // D55d-uat3 -- moduleKey null == universal (my_assets, etc.)
    if (m.moduleKey === null || m.moduleKey === undefined) return true;
    if (!loaded || !modules) return true;
    return modules[m.moduleKey] === true;
  });

  return (
    <nav
      data-testid="bottom-tab-bar"
      className="md:hidden fixed bottom-0 left-0 right-0 z-40 h-16 glass border-t border-[var(--surface-border)] flex items-stretch"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      {tabs.map((tab) => {
        const Icon = tab.icon;
        const active = tab.key === "home" ? loc.pathname === "/" : loc.pathname.startsWith(tab.to);
        const coming = tab.comingPhase != null;
        const onClick = (e) => {
          if (coming) {
            e.preventDefault();
            toast.info(t("common.comingInPhase", "Coming in Phase {{phase}}", { phase: tab.comingPhase }));
          }
        };
        return (
          <Link
            key={tab.key}
            to={coming ? "#" : tab.to}
            onClick={onClick}
            data-testid={`bottom-tab-${tab.key}`}
            className={[
              "flex-1 flex flex-col items-center justify-center gap-0.5 relative",
              active && !coming ? "text-sky" : "text-foreground/70",
              coming && "opacity-55",
            ].filter(Boolean).join(" ")}
          >
            <Icon className="h-5 w-5" />
            <span className="text-[10px] uppercase tracking-wider">{navLabel(t, tab.labelKey)}</span>
            {coming && (
              <span className="absolute top-1 right-3 text-[8px] px-1 py-0 rounded bg-white/10 text-foreground/70">
                P{tab.comingPhase}
              </span>
            )}
          </Link>
        );
      })}
      <Sheet>
        <SheetTrigger asChild>
          <button
            data-testid="bottom-tab-menu"
            className="flex-1 flex flex-col items-center justify-center gap-0.5 text-foreground/70"
            aria-label="More"
          >
            <MenuIcon className="h-5 w-5" />
            <span className="text-[10px] uppercase tracking-wider">{t("common.menu")}</span>
          </button>
        </SheetTrigger>
        <SheetContent side="left" className="w-[280px] bg-card/95 backdrop-blur-xl">
          <div className="flex items-center gap-3 pt-2 pb-6">
            <Logo size={32} kind="mark" />
            <span className="font-heading text-sm font-bold tracking-widest">SHARDA ENERGY OS</span>
          </div>
          <div className="space-y-1">
            {visible.map((m) => {
              const Icon = m.icon;
              return (
                <Link
                  key={m.key}
                  to={m.to}
                  data-testid={`mobile-menu-${m.key}`}
                  className="flex items-center gap-3 rounded-2xl px-3 py-2.5 hover:bg-white/5"
                >
                  <Icon className="h-5 w-5" />
                  <span className="flex-1 text-sm font-medium">{t(m.labelKey)}</span>
                  {!m.enabled && (
                    <Badge variant="secondary" className="text-[10px] bg-white/10 border-0">P{m.phase}</Badge>
                  )}
                </Link>
              );
            })}
          </div>
        </SheetContent>
      </Sheet>
    </nav>
  );
}

export default BottomTabBar;
